import { SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import { StatBoxV2 } from '@feathr/components';

import DonationFormEngagementCard from './DonationFormEngagementCard';
import { useDonationFormReport } from './DonationFormReport.hooks';
import {
  deltaToChange,
  formatDeltaValue,
  type IKpiTileDef,
  kpiRowOne,
  kpiRowTwo,
} from './DonationFormReport.utils';
import DonationFormTrends from './DonationFormTrends';
import TimeframeControls from './TimeframeControls';

import * as styles from './DonationFormReport.css';

export interface IDonationFormReportProps {
  /** The donation form being reported on. */
  form: DonationForm;
  /** Builds account-scoped URLs; omitted for anonymous report viewers. */
  localUrl?: (url: string) => string;
}

/**
 * Report view for a single donation form: date-range picker + two rows of KPI tiles
 * (primary value + subline + previous-period delta). Stats are fetched via
 * `AggregatedStats.aggregate({ model: 'form' })` for both the selected window
 * and the immediately preceding window of equal length.
 */
function DonationFormReport({ form, localUrl }: Readonly<IDonationFormReportProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { currentStats, previousStats, preset, range, previousRange, setPreset, setRange } =
    useDonationFormReport({ form });

  function renderTile(def: IKpiTileDef): JSX.Element {
    const delta = def.deltaPercent(currentStats, previousStats);
    return (
      <StatBoxV2
        change={deltaToChange(delta)}
        changeValue={formatDeltaValue(delta)}
        description={def.subline(currentStats, t)}
        key={def.key}
        label={def.label(t)}
        name={def.key}
        primary={def.primary(currentStats)}
        tooltip={def.tooltip?.(t)}
      />
    );
  }

  return (
    <Stack className={styles.root} gap={theme.spacing['6']}>
      <TimeframeControls
        onPresetChange={setPreset}
        onRangeChange={setRange}
        preset={preset}
        range={range}
      />
      <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing={theme.spacing['4']}>
        {kpiRowOne.map(renderTile)}
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, md: 3 }} spacing={theme.spacing['4']}>
        {kpiRowTwo.map(renderTile)}
      </SimpleGrid>
      <DonationFormTrends
        currentStats={currentStats}
        form={form}
        previousRange={previousRange}
        range={range}
      />
      <DonationFormEngagementCard
        dateRange={{ start: range[0], end: range[1] }}
        form={form}
        localUrl={localUrl}
      />
    </Stack>
  );
}

export default observer(DonationFormReport);
